/**
 * Episode upload queue with concurrency limit.
 * Status: pending -> uploading -> saving -> success | failed | cancelled
 */

export const TASK_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  SAVING: 'saving',
  SUCCESS: 'success',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
}

let uidSeq = 0

export function createTaskId() {
  uidSeq += 1
  return `ep_${Date.now()}_${uidSeq}`
}

/**
 * @param {Object} options
 * @param {number} [options.concurrency=2]
 * @param {Function} [options.onChange]
 */
export function createEpisodeUploadQueue(options = {}) {
  const concurrency = options.concurrency > 0 ? options.concurrency : 2
  const onChange = typeof options.onChange === 'function' ? options.onChange : () => {}

  const tasks = []
  let running = 0
  let stopped = false
  const abortMap = {}

  function snapshot() {
    const counts = {
      total: tasks.length,
      pending: 0,
      uploading: 0,
      saving: 0,
      success: 0,
      failed: 0,
      cancelled: 0
    }
    tasks.forEach(t => {
      if (counts[t.status] !== undefined) {
        counts[t.status] += 1
      }
    })
    const done = counts.success + counts.failed + counts.cancelled
    const percent = counts.total === 0 ? 0 : Math.round((counts.success / counts.total) * 100)
    return {
      tasks: tasks.slice(),
      counts,
      done,
      percent,
      active: running > 0 || counts.pending > 0,
      stopped
    }
  }

  function emit() {
    onChange(snapshot())
  }

  function findTask(taskId) {
    return tasks.find(t => t.id === taskId)
  }

  function enqueue(taskInput) {
    const task = {
      id: taskInput.id || createTaskId(),
      kind: taskInput.kind || 'video',
      name: taskInput.name || '',
      file: taskInput.file,
      serialNumber: taskInput.serialNumber,
      subtitleLang: taskInput.subtitleLang || null,
      videoId: taskInput.videoId,
      status: TASK_STATUS.PENDING,
      percent: 0,
      url: '',
      fileId: '',
      error: '',
      raw: taskInput.raw || null,
      runner: taskInput.runner,
      saver: taskInput.saver
    }
    tasks.push(task)
    emit()
    pump()
    return task.id
  }

  function enqueueMany(list) {
    const ids = []
    ;(list || []).forEach(item => {
      ids.push(enqueue(item))
    })
    return ids
  }

  function setStatus(task, status, patch) {
    task.status = status
    if (patch) {
      Object.keys(patch).forEach(k => {
        task[k] = patch[k]
      })
    }
    emit()
  }

  function pump() {
    if (stopped) {
      return
    }
    while (running < concurrency) {
      const next = tasks.find(t => t.status === TASK_STATUS.PENDING)
      if (!next) {
        break
      }
      running += 1
      runTask(next).finally(() => {
        running -= 1
        pump()
        emit()
      })
    }
    emit()
  }

  async function runTask(task) {
    if (stopped || abortMap[task.id] === 'cancel') {
      setStatus(task, TASK_STATUS.CANCELLED, { error: 'cancelled' })
      return
    }
    setStatus(task, TASK_STATUS.UPLOADING, { percent: 0, error: '' })
    try {
      if (!task.runner || typeof task.runner !== 'function') {
        throw new Error('missing upload runner')
      }
      const result = await task.runner(task, {
        onProgress: (p) => {
          if (abortMap[task.id] === 'cancel') {
            return
          }
          const percent = typeof p === 'number' ? p : (p && p.percent) || 0
          task.percent = Math.min(99, Math.max(0, Math.round(percent)))
          emit()
        },
        isCancelled: () => stopped || abortMap[task.id] === 'cancel'
      })
      if (stopped || abortMap[task.id] === 'cancel') {
        setStatus(task, TASK_STATUS.CANCELLED, { error: 'cancelled' })
        return
      }
      if (task.saver && typeof task.saver === 'function') {
        setStatus(task, TASK_STATUS.SAVING, { percent: 99 })
        await task.saver(task, result || {})
      }
      setStatus(task, TASK_STATUS.SUCCESS, {
        percent: 100,
        url: (result && result.url) || task.url || '',
        fileId: (result && result.fileId) || task.fileId || '',
        error: ''
      })
    } catch (e) {
      if (stopped || abortMap[task.id] === 'cancel') {
        setStatus(task, TASK_STATUS.CANCELLED, { error: 'cancelled' })
        return
      }
      setStatus(task, TASK_STATUS.FAILED, {
        error: (e && e.message) || String(e) || 'upload failed'
      })
    }
  }

  function retry(taskId) {
    const task = findTask(taskId)
    if (!task) {
      return
    }
    if (task.status !== TASK_STATUS.FAILED && task.status !== TASK_STATUS.CANCELLED) {
      return
    }
    delete abortMap[taskId]
    stopped = false
    setStatus(task, TASK_STATUS.PENDING, { percent: 0, error: '', url: '', fileId: '' })
    pump()
  }

  function retryAllFailed() {
    tasks.forEach(t => {
      if (t.status === TASK_STATUS.FAILED) {
        delete abortMap[t.id]
        t.status = TASK_STATUS.PENDING
        t.percent = 0
        t.error = ''
      }
    })
    stopped = false
    emit()
    pump()
  }

  function cancelPending() {
    tasks.forEach(t => {
      if (t.status === TASK_STATUS.PENDING) {
        t.status = TASK_STATUS.CANCELLED
        t.error = 'cancelled'
      } else if (t.status === TASK_STATUS.UPLOADING || t.status === TASK_STATUS.SAVING) {
        abortMap[t.id] = 'cancel'
      }
    })
    emit()
  }

  function cancelAll() {
    stopped = true
    cancelPending()
    tasks.forEach(t => {
      if (t.status === TASK_STATUS.UPLOADING || t.status === TASK_STATUS.SAVING) {
        abortMap[t.id] = 'cancel'
      }
    })
    emit()
  }

  function clearFinished() {
    for (let i = tasks.length - 1; i >= 0; i--) {
      const s = tasks[i].status
      if (s === TASK_STATUS.SUCCESS || s === TASK_STATUS.CANCELLED) {
        tasks.splice(i, 1)
      }
    }
    emit()
  }

  function reset() {
    cancelAll()
    tasks.splice(0, tasks.length)
    running = 0
    stopped = false
    Object.keys(abortMap).forEach(k => delete abortMap[k])
    emit()
  }

  function getSnapshot() {
    return snapshot()
  }

  return {
    enqueue,
    enqueueMany,
    retry,
    retryAllFailed,
    cancelPending,
    cancelAll,
    clearFinished,
    reset,
    getSnapshot,
    attachHandlers(taskId, { runner, saver }) {
      const task = findTask(taskId)
      if (!task) {
        return
      }
      if (runner) {
        task.runner = runner
      }
      if (saver) {
        task.saver = saver
      }
    }
  }
}

/**
 * Parse episode number from file name: 1.mp4 or videoId-1.mp4
 */
export function parseSerialNumber(fileName) {
  if (!fileName) {
    return null
  }
  const base = String(fileName).split('.')[0]
  let raw
  if (/-/.test(base)) {
    raw = base.split('-').pop()
  } else {
    raw = base
  }
  const num = parseInt(raw, 10)
  return Number.isNaN(num) ? null : num
}
